/*
 * Copyright (c) 2021 CalAmp Corp. All Rights Reserved
*/
import React, {useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {AugmentedSmarterTrailerSensorData} from '@ion/core/src/types/entities';
import {getAccordionWrapperStyles} from '@ion/dls/src/components/accordion/Accordion';
import {TombstoneLoader} from '@ion/dls/src/components/loading/TombstoneLoader';
import EventTime from '@ion/dls/src/components/time/EventTime';
import {CargoSenseIcon} from '@ion/dls/src/icons/CargoSenseIcon';
import {themedClassName} from '@ion/dls/src/theme/helpers';
import {dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {useThemeContext} from '@ion/dls/src/theme/useThemeContext';
import {DLSTheme} from '@ion/dls/src/types/types';
import {useSectionStyles} from './sectionStyles';
import {calculateTotalZoneAvailability} from './SmarterTrailerUtils';

export interface CargoSenseSectionProps {
  trailerData: AugmentedSmarterTrailerSensorData;
  sensorUpdateLoading?: boolean;
}

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    detailTombstone : {
      height  : '250px',
      padding : '10px'
    },
    cargoHeader : {
      display        : 'flex',
      justifyContent : 'center',
      alignItems     : 'center'
    },
    cargoHeaderTitle : {
      marginRight : '15px',
      fontFamily  : 'Roboto',
      fontWeight  : 600,
      fontSize    : '14px',
      lineHeight  : '17px',
      color       : dlsColor('black')
    },
    content : {
      padding       : '15px',
      display       : 'flex',
      flexDirection : 'column',
      alignItems    : 'center'
    },
    iconWrapper : {
      width                          : '300px',
      [theme.breakpoints.down('sm')] : {
        width   : '90%',
        '& svg' : {
          width : '100%'
        }
      }
    },
    dataDisplay : {
      display                        : 'flex',
      alignItems                     : 'center',
      justifyContent                 : 'space-between',
      width                          : '100%',
      padding                        : '0 15px',
      [theme.breakpoints.down('sm')] : {
        width : '100%'
      }
    },
    dataDisplayItem : {
      display        : 'flex',
      justifyContent : 'center',
      flexDirection  : 'column'
    },
    messageTime : {
      fontFamily    : 'Roboto',
      fontWeight    : 700,
      fontSize      : '12px',
      color         : dlsColor('grey2'),
      letterSpacing : '1px',
      margin        : '2.5px 0px',
      textTransform : 'uppercase'
    },
    messageTimeDiff : {
      ...dlsTypography('body3'),
      color      : dlsColor('grey3'),
      fontWeight : 400,
      fontSize   : '12px',
      lineHeight : '16px',
      margin     : '-2px 0px'
    },
    totalAvailability : {
      fontFamily    : 'Roboto',
      fontWeight    : 500,
      fontSize      : '10px',
      letterSpacing : '1px',
      textTransform : 'uppercase',
      color         : dlsColor('black'),
      background    : dlsColor('grey6'),
      padding       : '6px',
      width         : 'fit-content',
      margin        : '0px',
      borderRadius  : '2px',
      border        : `1px solid ${dlsColor('grey5')}`
    },
    ...getAccordionWrapperStyles()
  };
});

/**
 *  Tires section of the smarter trailer content. Expandable Accordion item
 *  that contains the tire overview content and trailer icon.
 *
 * ```typescript
 *  import {CargoSenseSection} from '@ion/features/src/smarterTrailer';
 *
 *  <CargoSenseSection asset={selectedAsset}
 *    trailerData={trailerData}
 *    selectedTire='trailerBackLeftInside'
 *    sensorUpdateLoading={isUpdatingSensorData}
 *    onChange={onToggleExpand} />
 * ```
 */
export const CargoSenseSection = ({
  trailerData,
  sensorUpdateLoading
}: CargoSenseSectionProps) => {
  const classes                      = useStyles();
  const sectionClasses               = useSectionStyles();
  const {isCurrent}                  = useThemeContext();
  const [fillValues, setFillValues]  = useState({});
  const {CargoSense: cargoSenseData} = trailerData;
  const {
    0: {
      messageTime
    }
  } = cargoSenseData;
  const totalAvailability = calculateTotalZoneAvailability(cargoSenseData);

  // Update the fill value for each zone inside the CargoSenseIcon.
  useEffect(() => {
    const updatedFillValues = {};
    for (const [key, cargosenseUnit] of Object.entries(cargoSenseData)) {
      if (key !== 'undefined' && 'height' in cargosenseUnit) {
        const {height: {value}} = cargosenseUnit;
        updatedFillValues[`zone${parseInt(key) + 1}Fill`] = parseInt(value);
      }
    }
    setFillValues(updatedFillValues);
  }, [cargoSenseData]);

  if (!sensorUpdateLoading) {
    return (
      <div className={themedClassName(classes.content, sectionClasses.currentContent, isCurrent)}>
        <CargoSenseIcon {...fillValues} wrapperClass={classes.iconWrapper} />
        <div className={themedClassName(classes.dataDisplay, sectionClasses.currentContent, isCurrent)}>
          <EventTime eventTime={messageTime} />
          <div className={classes.dataDisplayItem}>
            <p className={themedClassName(classes.totalAvailability, sectionClasses.currentLabel, isCurrent)}>
              {translate('common:smarterTrailer.cargoSense.totalAvailability', {totalAvailability})}
            </p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className={classes.detailTombstone}>
      <TombstoneLoader />
    </div>
  );
};

export default CargoSenseSection;
